<template>
  <div class="analysis">
    <div v-if="loading" class="loading">
      <div class="loading-spinner">⏳</div>
      <p>正在加载分析结果...</p>
    </div>

    <div v-else-if="error" class="error">
      <div class="error-icon">❌</div>
      <h2>加载失败</h2>
      <p>{{ error }}</p>
      <button @click="loadAnalysis" class="retry-btn">重试</button>
    </div>

    <div v-else-if="analysis" class="analysis-content">
      <div class="analysis-header">
        <router-link to="/tasks" class="back-btn">← 返回任务列表</router-link>
        <h1>{{ analysis.video_meta.title || '视频分析结果' }}</h1>
        <div class="analysis-meta">
          <span>平台: {{ getPlatformName(analysis.video_meta.platform) }}</span>
          <span>作者: {{ analysis.video_meta.author }}</span>
          <span>分析时间: {{ formatDate(analysis.created_at) }}</span>
        </div>
      </div>

      <div class="analysis-sections">
        <!-- 一、视频基础信息 -->
        <section class="section info-section">
          <h2>📹 视频基础信息</h2>
          <div class="info-grid">
            <div class="info-item">
              <label>平台:</label>
              <span class="platform-badge">{{ getPlatformName(analysis.video_meta.platform) }}</span>
            </div>
            <div class="info-item">
              <label>标题:</label>
              <span class="info-value">{{ analysis.video_meta.title }}</span>
            </div>
            <div class="info-item">
              <label>时长:</label>
              <span class="info-value">{{ formatDuration(analysis.video_meta.duration) }}</span>
            </div>
            <div class="info-item" v-if="analysis.video_meta.video_url">
              <label>源视频:</label>
              <div class="video-actions">
                <a :href="analysis.video_meta.video_url" target="_blank" class="btn-preview">
                  👁️ 预览
                </a>
                <a :href="analysis.video_meta.video_url" :download="`${analysis.video_meta.title}.mp4`" class="btn-download">
                  📥 下载
                </a>
              </div>
            </div>
            <div class="info-item">
              <label>互动数据:</label>
              <span class="info-value">
                👁️ {{ formatNumber(analysis.video_meta.view_count) }} 
                👍 {{ formatNumber(analysis.video_meta.like_count) }}
              </span>
            </div>
          </div>
        </section>

        <!-- 二、关键帧交付 -->
        <section class="section keyframes-delivery">
          <div class="section-header">
            <h2>🖼️ 关键帧交付</h2>
            <div class="header-actions">
              <button @click="downloadAllFrames" class="btn-primary">
                📥 下载全部关键帧 (ZIP)
              </button>
            </div>
          </div>

          <div class="delivery-stats">
            <div class="stat-item">
              <span class="stat-label">✅ 总帧数</span>
              <span class="stat-value">{{ keyframes.length }} 帧</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">✅ 抽帧策略</span>
              <span class="stat-value">每5秒智能提取 + 视觉显著性分析</span>
            </div>
          </div>

          <!-- Debug Info -->
          <div style="background: #fff3cd; padding: 1rem; margin: 1rem 0; border-radius: 4px; font-family: monospace; font-size: 0.9rem;">
            <strong>🔍 调试信息：</strong><br>
            scenes存在: {{ !!analysis.scenes }}<br>
            scenes数量: {{ analysis.scenes?.length || 0 }}<br>
            keyframes数量: {{ keyframes.length }}<br>
            <button @click="console.log('analysis:', analysis)" style="margin-top: 0.5rem; padding: 0.25rem 0.5rem;">打印完整数据到控制台</button>
          </div>

          <!-- AI 视频拆解表格 -->
        <section v-if="(analysis.scenes && analysis.scenes.length > 0) || keyframes.length > 0" class="section script-table-section">
          <div class="section-header">
            <h2>🎬 AI 视频拆解</h2>
            <div class="header-actions">
              <button @click="exportScriptTable" class="btn-secondary">
                📊 导出表格
              </button>
            </div>
          </div>
          
          <div class="script-table-container">
            <table class="script-table">
              <thead>
                <tr>
                  <th width="15%">脚本概述</th>
                  <th width="20%">画面片段</th>
                  <th width="25%">情景解读</th>
                  <th width="20%">台词/口播</th>
                  <th width="20%">拍摄技巧</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(scene, index) in analysis.scenes" :key="index">
                  <!-- 脚本概述 -->
                  <td class="cell-summary">
                    <div class="time-range">{{ scene.timestamp || formatTimeRange(index) }}</div>
                    <div class="scene-title">{{ scene.summary || scene.title || `场景 ${index + 1}` }}</div>
                  </td>
                  
                  <!-- 画面片段 (关键帧) -->
                  <td class="cell-visual">
                    <div class="visual-wrapper">
                      <img 
                        :src="getKeyframeUrl(scene.keyframe_index, index)" 
                        class="table-thumbnail" 
                        @click="previewFrame(getKeyframe(scene.keyframe_index, index))"
                        loading="lazy"
                      />
                      <div class="visual-actions">
                        <button 
                          @click.stop="previewFrame(getKeyframe(scene.keyframe_index, index))" 
                          class="btn-icon" 
                          title="预览"
                        >
                          👁️
                        </button>
                        <button 
                          @click.stop="downloadFrame(getKeyframe(scene.keyframe_index, index), scene.keyframe_index || index)" 
                          class="btn-icon" 
                          title="下载"
                        >
                          📥
                        </button>
                      </div>
                    </div>
                  </td>
                  
                  <!-- 情景解读 -->
                  <td class="cell-description">
                    <p>{{ scene.description }}</p>
                  </td>
                  
                  <!-- 台词/口播 -->
                  <td class="cell-dialogue">
                    <p v-if="scene.dialogue">{{ scene.dialogue }}</p>
                    <p v-else class="empty-text">--</p>
                  </td>
                  
                  <!-- 拍摄技巧 -->
                  <td class="cell-technique">
                    <div class="technique-tags">
                      <span v-if="scene.technique" class="tech-tag">{{ scene.technique }}</span>
                      <span v-else-if="scene.shot_type" class="tech-tag">{{ scene.shot_type }}</span>
                      <span v-else class="empty-text">--</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

          <!-- 高潜力候选帧 -->
          <div v-if="highPotentialFrames.length > 0" class="high-potential-section">
            <h3>🔥 高潜力封面候选</h3>
            <div class="potential-grid">
              <div v-for="(frame, idx) in highPotentialFrames" :key="idx" class="potential-card">
                <img :src="frame.url" class="potential-img" />
                <div class="potential-info">
                  <span class="time-badge">{{ formatTime(frame.timestamp) }}</span>
                  <p class="reason">{{ frame.reason }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- 关键帧预览画廊 -->
          <div class="gallery-container">
            <h3>关键帧预览</h3>
            <div class="horizontal-scroll">
              <div 
                v-for="(frame, index) in keyframes" 
                :key="index"
                class="keyframe-card"
              >
                <div class="thumbnail-wrapper" @click="previewFrame(frame)">
                  <img :src="frame.url" loading="lazy" />
                  <div class="timestamp-badge">{{ formatTime(frame.timestamp) }}</div>
                </div>
                <div class="frame-desc">{{ frame.description || '场景描述...' }}</div>
              </div>
            </div>
          </div>
        </section>

        <!-- Prompt详情弹窗 -->
        <div v-if="showPromptModal" class="modal-overlay" @click="closePromptModal">
          <div class="modal-content" @click.stop>
            <div class="modal-header">
              <h3>AI 图片提示词</h3>
              <button @click="closePromptModal" class="close-btn">✕</button>
            </div>
            <div class="modal-body">
              <img v-if="selectedFrame" :src="selectedFrame.url" class="modal-image" />
              <div class="prompt-detail">
                <h4>时间戳: {{ selectedFrame ? formatTime(selectedFrame.timestamp) : '' }}</h4>
                <p class="prompt-full">{{ selectedFrame?.prompt }}</p>
                <button @click="copyPrompt(selectedFrame?.prompt)" class="btn-copy-full">
                  📋 复制完整Prompt
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 三、爆款内容分析 -->
        <section class="section viral-analysis">
          <h2>🔥 爆款内容分析</h2>
          
          <div class="analysis-grid">
            <div class="analysis-card">
              <h4>🎣 钩子类型</h4>
              <p class="analysis-value">{{ analysis.hook_type || '悬念式 + 痛点式' }}</p>
            </div>
            <div class="analysis-card">
              <h4>📈 情感曲线</h4>
              <p class="analysis-value">{{ analysis.emotion_trajectory || '平静 → 震惊 → 共鸣 → 行动' }}</p>
            </div>
            <div class="analysis-card">
              <h4>🥁 节奏密度</h4>
              <p class="analysis-value">{{ analysis.rhythm_density || '中' }} ({{ analysis.cut_frequency || '0.2' }} cuts/s)</p>
            </div>
            <div class="analysis-card">
              <h4>📖 视觉叙事模型</h4>
              <p class="analysis-value">{{ analysis.narrative_model || '问题-演示-结果 (PDR)' }}</p>
            </div>
          </div>

          <div class="viral-formula-box">
            <h4>🧪 爆款公式</h4>
            <p>{{ analysis.viral_formula || '痛点引入 (3s) + 极限测试 (15s) + 意外反转 (45s) + 价值升华 (End)' }}</p>
          </div>

          <!-- 增强爆款分析面板 -->
          <EnhancedViralPanel 
            v-if="analysis.viral_score !== undefined"
            :hook-strength="analysis.hook_strength"
            :rhythm-density="typeof analysis.rhythm_density === 'number' ? analysis.rhythm_density : undefined"
            :emotion-curve="Array.isArray(analysis.emotion_curve) ? analysis.emotion_curve : undefined"
            :completion-rate="analysis.completion_rate"
            :viral-probability="analysis.viral_probability"
          />
        </section>

        <!-- 四、AI 图像提示词 -->
        <section class="section ai-prompts">
          <h2>🎨 AI 图像提示词 (高潜力帧专用)</h2>
          <div class="prompts-list">
            <div v-for="(frame, idx) in highPotentialFrames" :key="idx" class="prompt-item">
              <div class="prompt-header">
                <span class="time-tag">Time: {{ formatTime(frame.timestamp) }}</span>
                <button @click="copyPrompt(frame.prompt)" class="btn-copy-sm">复制</button>
              </div>
              <div class="prompt-content">
                <div class="scene-desc"><strong>Scene:</strong> {{ frame.description }}</div>
                <div class="prompt-text"><strong>Prompt:</strong> "{{ frame.prompt }}"</div>
              </div>
            </div>
          </div>
        </section>

        <!-- 五、AI 写作指令模板 -->
        <section class="section ai-writing">
          <h2>✍️ AI 写作指令模板</h2>
          <div class="template-box">
            <p>“请生成一个类似风格的短视频脚本，开头使用‘<strong>{{ analysis.hook_type || '悬念' }}</strong>’钩子，在<strong>{{ Math.floor(analysis.video_meta.duration / 2) }}</strong>秒处插入反转，结尾引导用户<strong>关注评论</strong>……”</p>
            <button class="btn-copy-template" @click="copyTemplate">复制指令</button>
          </div>
        </section>

        <!-- 爆款指数 (旧版，保留兼容) -->
        <section v-if="analysis.viral_score" class="section">
          <h2>📊 爆款指数</h2>
          <div class="viral-score">
            <div class="score-circle">
              <div class="score-value">{{ Math.round(analysis.viral_score * 100) }}%</div>
              <div class="score-label">爆款潜力</div>
            </div>
            <div class="score-description">
              <p>该视频具有{{ getViralLevel(analysis.viral_score) }}的爆款潜力</p>
              <p>{{ getViralAdvice(analysis.viral_score) }}</p>
            </div>
          </div>
        </section>

        <!-- 文字记录 -->
        <section v-if="analysis.transcript" class="section">
          <h2>📄 文字记录</h2>
          <div class="transcript-content">
            <pre>{{ analysis.transcript }}</pre>
          </div>
        </section>

        <!-- 原始数据 -->
        <section class="section">
          <h2>🔧 原始数据</h2>
          <div class="raw-data">
            <pre>{{ JSON.stringify(analysis, null, 2) }}</pre>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { resolveApiUrl } from '@/utils/api'
import EnhancedViralPanel from '@/components/EnhancedViralPanel.vue'


interface VideoMeta {
  id: string
  platform: string
  title: string
  author: string
  description?: string
  duration: number
  video_url: string
  thumbnail_url?: string
  view_count?: number
  like_count?: number
  comment_count?: number
  share_count?: number
  tags: string[]
}

interface Scene {
  title?: string
  summary?: string
  description: string
  timestamp?: number | string
  dialogue?: string
  technique?: string
  shot_type?: string
  keyframe_index?: number
}

interface AnalysisData {
  task_id: string
  video_meta: VideoMeta
  status: string
  progress?: number
  created_at: string
  updated_at: string
  error_message?: string
  storyline?: string
  scenes: Scene[]
  creation_techniques: string[]
  viral_factors: string[]
  viral_score?: number
  transcript?: string
  
  // Viral analysis fields
  hook_type?: string
  emotion_trajectory?: string
  rhythm_density?: string | number
  cut_frequency?: number | string
  narrative_model?: string
  viral_formula?: string
  hook_strength?: number
  emotion_curve?: any
  completion_rate?: number
  viral_probability?: number
}

const route = useRoute()
const loading = ref(true)
const error = ref('')
interface Keyframe {
  index: number
  url: string
  timestamp: number
  path: string
  description?: string
  tags?: string[]
  prompt?: string
  type_label?: string
  reason?: string
}

const analysis = ref<AnalysisData | null>(null)
const keyframes = ref<Keyframe[]>([])

const loadAnalysis = async () => {
  const taskId = route.params.taskId as string
  
  if (!taskId) {
    error.value = '缺少任务ID'
    loading.value = false
    return
  }

  try {
    const response = await fetch(resolveApiUrl(`/api/video/task/${taskId}`))
    
    if (!response.ok) {
      throw new Error(`加载失败: ${response.statusText}`)
    }

    const data = await response.json()
    analysis.value = data
    
    console.log('[Analysis] Loaded data:', {
      hasScenes: !!data.scenes,
      scenesCount: data.scenes?.length || 0,
      scenes: data.scenes
    })
    
    // 加载关键帧
    await loadKeyframes(taskId)
    
    // 如果没有 scenes 数据，从关键帧生成
    if (!analysis.value?.scenes || analysis.value.scenes.length === 0) {
      console.log('[Analysis] No scenes data, generating from keyframes')
      generateScenesFromKeyframes()
    }
    
  } catch (err) {
    error.value = err instanceof Error ? err.message : '加载分析结果时发生错误'
    console.error('Analysis load error:', err)
  } finally {
    loading.value = false
  }
}

const loadKeyframes = async (taskId: string) => {
  try {
    const url = resolveApiUrl(`/api/frames/${taskId}`)
    console.log(`[Analysis] Fetching keyframes from: ${url}`)
    
    const response = await fetch(url)
    console.log(`[Analysis] Keyframes response status: ${response.status}`)
    
    if (response.ok) {
      const data = await response.json()
      console.log(`[Analysis] Keyframes data:`, data)
      
      if (!data.frames || !Array.isArray(data.frames)) {
        console.error('[Analysis] Invalid keyframes data format:', data)
        return
      }
      
      keyframes.value = data.frames.map((frame: any) => ({
        ...frame,
        url: resolveApiUrl(frame.url),
        prompt: frame.description || 'AI Prompt 生成中...',
        type_label: frame.frame_type || '关键帧',
        tags: frame.tags || []
      }))
      
      console.log(`[Analysis] Loaded ${keyframes.value.length} keyframes`)
      
      // 自动识别高潜力帧（如果没有后端数据）
      if (keyframes.value.length > 0 && !highPotentialFrames.value.length) {
        identifyHighPotentialFrames()
      }
    } else {
      const errorText = await response.text()
      console.error(`[Analysis] Failed to load keyframes: ${response.status} ${errorText}`)
    }
  } catch (err) {
    console.error('Failed to load keyframes:', err)
  }
}

const generateScenesFromKeyframes = () => {
  if (!keyframes.value || keyframes.value.length === 0 || !analysis.value) {
    console.log('[Analysis] No keyframes to generate scenes from or analysis not loaded')
    return
  }
  
  analysis.value.scenes = keyframes.value.map((frame: any, index: number) => {
    const start = Math.floor(frame.timestamp)
    const end = Math.min(start + 5, analysis.value?.video_meta?.duration || start + 5)
    
    return {
      timestamp: `${Math.floor(start/60).toString().padStart(2,'0')}:${(start%60).toString().padStart(2,'0')}-${Math.floor(end/60).toString().padStart(2,'0')}:${(end%60).toString().padStart(2,'0')}`,
      summary: `场景 ${index + 1}`,
      description: frame.prompt || frame.description || `${frame.type_label}画面`,
      dialogue: '',
      technique: `${frame.type_label}镜头`,
      keyframe_index: index
    }
  })
  
  console.log(`[Analysis] Generated ${analysis.value.scenes.length} scenes from keyframes`)
}

const highPotentialFrames = ref<any[]>([])

const identifyHighPotentialFrames = () => {
  // 简单的逻辑：选择开头、中间、结尾的帧作为候选
  if (keyframes.value.length < 3) {
    highPotentialFrames.value = keyframes.value
    return
  }
  
  const first = keyframes.value[0]
  const middle = keyframes.value[Math.floor(keyframes.value.length / 2)]
  const last = keyframes.value[keyframes.value.length - 1]
  
  highPotentialFrames.value = [
    { ...first, reason: '开场黄金3秒，吸引注意力' },
    { ...middle, reason: '视频高潮部分，情绪最强' },
    { ...last, reason: '结尾引导关注' }
  ]
}

const downloadAllFrames = () => {
  // 模拟下载链接，实际应该调用后端打包接口
  alert('正在打包下载所有关键帧...')
}

const getKeyframe = (index?: number, fallbackIndex?: number) => {
  const idx = index !== undefined ? index : fallbackIndex || 0
  return keyframes.value[idx] || keyframes.value[0] || {}
}

const getKeyframeUrl = (index?: number, fallbackIndex?: number) => {
  const frame = getKeyframe(index, fallbackIndex)
  return frame.url || ''
}

const formatTimeRange = (index: number) => {
  const start = index * 5
  const end = start + 5
  return `${formatTime(start)} - ${formatTime(end)}`
}

const exportScriptTable = () => {
  // 简单的 CSV 导出
  const headers = ['时间', '脚本概述', '情景解读', '台词', '拍摄技巧']
  const rows = (analysis.value?.scenes || []).map((scene: any, index: number) => [
    scene.timestamp || formatTimeRange(index),
    scene.summary || scene.title || `场景 ${index + 1}`,
    scene.description,
    scene.dialogue || '',
    scene.technique || scene.shot_type || ''
  ])
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(','))
  ].join('\n')
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = `${analysis.value?.video_meta.title || 'video'}_script.csv`
  link.click()
}

const copyTemplate = async () => {
  if (!analysis.value) return
  
  const template = `请生成一个类似风格的短视频脚本，开头使用‘${analysis.value?.hook_type || '悬念'}’钩子，在${Math.floor((analysis.value?.video_meta.duration || 0) / 2)}秒处插入反转，结尾引导用户关注评论……`
  
  try {
    await navigator.clipboard.writeText(template)
    alert('写作指令已复制！')
  } catch (err) {
    console.error('Failed to copy:', err)
  }
}

const downloadFrame = async (frame: any, index: number) => {
  const taskId = route.params.taskId as string
  const downloadUrl = resolveApiUrl(`/api/frames/download/${taskId}/${index}`)
  window.open(downloadUrl, '_blank')
}

const previewFrame = (frame: any) => {
  // 在新窗口中打开预览
  window.open(frame.url, '_blank')
}

// Modal state
const showPromptModal = ref(false)
const selectedFrame = ref<any>(null)

const viewPromptDetail = (frame: any) => {
  selectedFrame.value = frame
  showPromptModal.value = true
}

const closePromptModal = () => {
  showPromptModal.value = false
  selectedFrame.value = null
}

const copyPrompt = async (prompt: string) => {
  if (!prompt) return
  
  try {
    await navigator.clipboard.writeText(prompt)
    // 可以添加提示
    alert('Prompt已复制到剪贴板！')
  } catch (err) {
    console.error('复制失败:', err)
    // 降级方案
    const textarea = document.createElement('textarea')
    textarea.value = prompt
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
    alert('Prompt已复制到剪贴板！')
  }
}

const exportPrompts = (format: 'txt' | 'csv') => {
  const taskId = route.params.taskId as string
  
  if (format === 'txt') {
    const content = keyframes.value
      .map((frame, index) => {
        const header = `[${index + 1}] ${formatTime(frame.timestamp)}`
        const tags = frame.tags ? `标签: ${frame.tags.join(', ')}` : ''
        const prompt = frame.prompt || '暂无Prompt'
        return `${header}\n${tags}\n${prompt}\n`
      })
      .join('\n---\n\n')
    
    downloadFile(content, `prompts_${taskId}.txt`, 'text/plain')
  } else {
    const csvRows = [
      ['序号', '时间戳', '类型', '标签', 'Prompt'],
      ...keyframes.value.map((frame, index) => [
        (index + 1).toString(),
        formatTime(frame.timestamp),
        frame.type_label || '内容',
        frame.tags ? frame.tags.join('; ') : '',
        frame.prompt || ''
      ])
    ]
    
    const csv = csvRows.map(row => 
      row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(',')
    ).join('\n')
    
    downloadFile(csv, `prompts_${taskId}.csv`, 'text/csv;charset=utf-8')
  }
}

const downloadFile = (content: string, filename: string, type: string) => {
  const blob = new Blob(['\uFEFF' + content], { type }) // 添加BOM for UTF-8
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleString('zh-CN')
}

const formatDuration = (seconds: number): string => {
  if (!seconds) return '未知'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = Math.floor(seconds % 60)
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

const formatNumber = (num?: number): string => {
  if (!num) return '0'
  if (num >= 10000) {
    return (num / 10000).toFixed(1) + 'w'
  }
  return num.toString()
}

const formatTime = (seconds?: number): string => {
  if (!seconds) return '未知'
  const minutes = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

const getPlatformName = (platform: string): string => {
  const platformMap: Record<string, string> = {
    'douyin': '抖音',
    'xhs': '小红书',
    'bili': 'B站',
    'youtube': 'YouTube',
    'ks': '快手'
  }
  return platformMap[platform] || platform
}

const getViralLevel = (score: number): string => {
  if (score >= 0.8) return '极高'
  if (score >= 0.6) return '较高'
  if (score >= 0.4) return '中等'
  if (score >= 0.2) return '较低'
  return '很低'
}

const getViralAdvice = (score: number): string => {
  if (score >= 0.8) return '该视频内容优质，具有很强的传播潜力，建议积极推广'
  if (score >= 0.6) return '该视频有较好的传播基础，可以适当优化推广策略'
  if (score >= 0.4) return '该视频有一定潜力，建议在内容或形式上进一步优化'
  if (score >= 0.2) return '该视频需要较大改进才能获得更好的传播效果'
  return '建议重新审视内容策略，提升视频质量和吸引力'
}

onMounted(() => {
  loadAnalysis()
})
</script>

<style scoped>
.analysis {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.loading {
  text-align: center;
  padding: 4rem 2rem;
}

.loading-spinner {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.loading p {
  color: #666;
  font-size: 1.1rem;
}

.error {
  text-align: center;
  padding: 4rem 2rem;
}

.error-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.error h2 {
  color: #e74c3c;
  margin-bottom: 1rem;
}

.error p {
  color: #666;
  margin-bottom: 2rem;
}

.retry-btn {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
}

.retry-btn:hover {
  background: #2980b9;
}

.analysis-header {
  margin-bottom: 2rem;
}

.back-btn {
  display: inline-block;
  color: #3498db;
  text-decoration: none;
  margin-bottom: 1rem;
  font-weight: 500;
}

.back-btn:hover {
  text-decoration: underline;
}

.analysis-header h1 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.analysis-meta {
  display: flex;
  gap: 2rem;
  color: #666;
  font-size: 0.9rem;
}

.section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.section h2 {
  color: #2c3e50;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #f8f9fa;
  padding-bottom: 0.5rem;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f8f9fa;
}

.info-item label {
  font-weight: 500;
  color: #2c3e50;
}

.video-description {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f8f9fa;
}

.video-description h4 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1rem;
}

.video-description p {
  line-height: 1.6;
  color: #666;
}

.video-link-section {
  margin-top: 1rem;
}

.video-link-section h4 {
  color: #2c3e50;
  margin-bottom: 0.5rem;
  font-size: 1rem;
}

.video-link {
  color: #3498db;
  text-decoration: none;
  word-break: break-all;
}

.video-link:hover {
  text-decoration: underline;
}

.techniques {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.technique-tag {
  background: #e8f5e8;
  color: #2e7d32;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid #c8e6c9;
}

.viral-factors {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.factor-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  background: #fff8e1;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
}

.factor-icon {
  font-size: 1.2rem;
  flex-shrink: 0;
}

.factor-text {
  flex: 1;
  color: #666;
  line-height: 1.5;
}

.viral-score {
  display: flex;
  align-items: center;
  gap: 2rem;
}

.score-circle {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(#4caf50 0deg, #4caf50 calc(var(--score) * 3.6deg), #f8f9fa calc(var(--score) * 3.6deg));
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
  border: 4px solid #f8f9fa;
}

.score-circle::before {
  content: '';
  position: absolute;
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 50%;
  z-index: 1;
}

.score-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #4caf50;
  z-index: 2;
}

.score-label {
  font-size: 0.8rem;
  color: #666;
  z-index: 2;
}

.score-description {
  flex: 1;
}

.score-description p {
  margin: 0.5rem 0;
  color: #666;
  line-height: 1.6;
}

.transcript-content {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  max-height: 400px;
  overflow-y: auto;
}

.transcript-content pre {
  margin: 0;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #666;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.scenes {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.scene-item {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #3498db;
}

.scene-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
}

.scene-description {
  color: #666;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

.scene-time {
  font-size: 0.8rem;
  color: #3498db;
  font-weight: 500;
}

.summary-content {
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #27ae60;
}

.summary-content p {
  margin: 0;
  line-height: 1.6;
  color: #666;
  font-size: 1.1rem;
}

.keywords {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.keyword-tag {
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
}

.sentiment-analysis {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.sentiment-score {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.score-item {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.score-item label {
  min-width: 80px;
  font-weight: 500;
}

.score-bar {
  flex: 1;
  height: 20px;
  background: #f8f9fa;
  border-radius: 10px;
  overflow: hidden;
}

.score-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.score-fill.positive {
  background: #27ae60;
}

.score-fill.neutral {
  background: #f39c12;
}

.score-fill.negative {
  background: #e74c3c;
}

.score-item span {
  min-width: 50px;
  text-align: right;
  font-weight: 500;
}

.sentiment-summary {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.sentiment-label {
  margin-left: 0.5rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
}

.sentiment-label.positive {
  background: #d4edda;
  color: #155724;
}

.sentiment-label.neutral {
  background: #fff3cd;
  color: #856404;
}

.sentiment-label.negative {
  background: #f8d7da;
  color: #721c24;
}

.scenes, .text-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.scene-item, .text-item {
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.scene-time, .text-time {
  min-width: 80px;
  font-weight: 500;
  color: #3498db;
}

.scene-description, .text-description {
  flex: 1;
  color: #666;
}

.raw-data {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  overflow-x: auto;
}

.raw-data pre {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  white-space: pre-wrap;
  word-wrap: break-word;
}

/* 智能关键帧画廊样式 */
.keyframes-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 16px;
  padding: 2rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.export-btn {
  padding: 0.5rem 1rem;
  background: white;
  border: 2px solid #667eea;
  color: #667eea;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.export-btn:hover {
  background: #667eea;
  color: white;
  transform: translateY(-2px);
}

.keyframes-gallery {
  position: relative;
}

.horizontal-scroll {
  display: flex;
  gap: 1.5rem;
  overflow-x: auto;
  padding: 1rem 0.5rem;
  scroll-snap-type: x mandatory;
  scrollbar-width: thin;
  scrollbar-color: #667eea #f0f0f0;
}

.horizontal-scroll::-webkit-scrollbar {
  height: 8px;
}

.horizontal-scroll::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 4px;
}

.horizontal-scroll::-webkit-scrollbar-thumb {
  background: #667eea;
  border-radius: 4px;
}

.keyframe-card {
  flex: 0 0 280px;
  scroll-snap-align: start;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  padding: 1rem;
  transition: all 0.3s;
}

.keyframe-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
}

.thumbnail-wrapper {
  position: relative;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 0.75rem;
  cursor: pointer;
  background: #f0f0f0;
}

.keyframe-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.thumbnail-wrapper:hover .keyframe-thumbnail {
  transform: scale(1.05);
}

.frame-badge {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(102, 126, 234, 0.9);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  backdrop-filter: blur(4px);
}

.timestamp-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
}

.tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  min-height: 24px;
}

.tag {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  color: #1976d2;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 500;
}

.prompt-section {
  background: #f8f9fa;
  padding: 0.75rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  min-height: 80px;
}

.prompt-text {
  font-size: 0.85rem;
  color: #555;
  margin-bottom: 0.5rem;
  line-height: 1.4;
  max-height: 42px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.prompt-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-copy, .btn-view {
  flex: 1;
  padding: 0.4rem;
  border: none;
  border-radius: 6px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.2s;
  font-weight: 500;
}

.btn-copy {
  background: #4caf50;
  color: white;
}

.btn-copy:hover {
  background: #45a049;
  transform: scale(1.05);
}

.btn-view {
  background: #2196f3;
  color: white;
}

.btn-view:hover {
  background: #1976d2;
  transform: scale(1.05);
}

.card-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  flex: 1;
  padding: 0.6rem;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-preview {
  background: linear-gradient(135deg, #ff9800 0%, #ff5722 100%);
  color: white;
}

.btn-preview:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(255, 152, 0, 0.4);
}

.btn-download {
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
}

.btn-download:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
}

/* Modal样式 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s;
}

.modal-content {
  background: white;
  border-radius: 16px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #999;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.modal-image {
  width: 100%;
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.prompt-detail h4 {
  color: #667eea;
  margin-bottom: 1rem;
}

.prompt-full {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 1rem;
}

.btn-copy-full {
  width: 100%;
  padding: 0.75rem;
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-copy-full:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.4);
}
/* 新增样式 */
.info-section .info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  background: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
}

.info-value {
  font-weight: 600;
  color: #2c3e50;
  font-size: 1.1rem;
}

.platform-badge {
  background: #e1f5fe;
  color: #0288d1;
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
}

.keyframes-delivery .delivery-stats {
  display: flex;
  gap: 2rem;
  margin-bottom: 1.5rem;
  background: #fff3e0;
  padding: 1rem;
  border-radius: 8px;
}

.stat-label {
  color: #e65100;
  font-weight: 600;
  margin-right: 0.5rem;
}

.high-potential-section {
  margin: 2rem 0;
}

.potential-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.potential-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.potential-card:hover {
  transform: translateY(-5px);
}

.potential-img {
  width: 100%;
  height: 160px;
  object-fit: cover;
}

.potential-info {
  padding: 1rem;
}

.time-badge {
  background: #2196f3;
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  margin-right: 0.5rem;
}

.reason {
  margin-top: 0.5rem;
  color: #555;
  font-size: 0.9rem;
  line-height: 1.4;
}

.analysis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.analysis-card {
  background: #f0f4c3;
  padding: 1.5rem;
  border-radius: 12px;
  text-align: center;
}

.analysis-card h4 {
  margin: 0 0 0.5rem 0;
  color: #827717;
}

.analysis-value {
  font-size: 1.1rem;
  font-weight: 600;
  color: #33691e;
}

.viral-formula-box {
  background: #e8eaf6;
  padding: 1.5rem;
  border-radius: 12px;
  margin-bottom: 2rem;
  border-left: 5px solid #3f51b5;
}

.prompts-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.prompt-item {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e0e0e0;
}

.prompt-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.time-tag {
  font-weight: 600;
  color: #666;
}

.btn-copy-sm {
  background: #eee;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
}

.prompt-content {
  font-family: monospace;
  color: #333;
}

.template-box {
  background: #e0f2f1;
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  border: 2px dashed #009688;
}

.template-box p {
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  color: #004d40;
}

.btn-copy-template {
  background: #009688;
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 24px;
  font-size: 1rem;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-copy-template:hover {
  background: #00796b;
}
/* 脚本表格样式 (专业版) */
.script-table-section {
  background: white;
  padding: 0;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  margin-top: 2rem;
}

.script-table-container {
  overflow-x: auto;
}

.script-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1000px; /* 确保不挤压 */
  font-size: 0.9rem;
}

.script-table th {
  background: #f1f3f4;
  padding: 12px 16px;
  text-align: left;
  font-weight: 700;
  color: #202124;
  border-bottom: 2px solid #dadce0;
  white-space: nowrap;
}

.script-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #dadce0;
  vertical-align: top;
  color: #3c4043;
}

.script-table tr:nth-child(even) {
  background: #f8f9fa;
}

.script-table tr:hover {
  background: #e8f0fe;
}

.cell-summary {
  font-weight: 600;
  color: #1a73e8;
}

.time-range {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.8rem;
  color: #5f6368;
  margin-bottom: 4px;
  display: block;
}

.scene-title {
  font-size: 1rem;
}

.visual-wrapper {
  position: relative;
  width: 180px;
  height: 101px; /* 16:9 */
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid #dadce0;
}

.table-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;
}

.visual-wrapper:hover .table-thumbnail {
  transform: scale(1.05);
}

.visual-actions {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  opacity: 0;
  transition: opacity 0.2s;
}

.visual-wrapper:hover .visual-actions {
  opacity: 1;
}

.btn-icon {
  background: white;
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1.2rem;
  transition: transform 0.2s;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.btn-icon:hover {
  transform: scale(1.1);
  background: #f1f3f4;
}

.cell-description p, .cell-dialogue p {
  margin: 0;
  line-height: 1.6;
}

.empty-text {
  color: #9aa0a6;
  font-style: italic;
}

.technique-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tech-tag {
  background: #e6f4ea;
  color: #137333;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  border: 1px solid #ceead6;
}

.btn-secondary {
  background: white;
  border: 1px solid #dadce0;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  color: #1a73e8;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-secondary:hover {
  background: #f1f3f4;
  border-color: #1a73e8;
}

/* 视频操作按钮 */
.video-actions {
  display: flex;
  gap: 8px;
}

.btn-preview,
.btn-download {
  padding: 6px 12px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-preview {
  background: #e8f0fe;
  color: #1a73e8;
  border: 1px solid #1a73e8;
}

.btn-preview:hover {
  background: #1a73e8;
  color: white;
}

.btn-download {
  background: #34a853;
  color: white;
  border: 1px solid #34a853;
}

.btn-download:hover {
  background: #2d8e47;
}
</style>
